import type { RouteLocationNormalizedLoaded } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useGamesStore } from '@nsftx/seven-client-retail-vue-sgp'
import { useDmCoreSpaceOperatorStore } from '@nsftx/seven-client-retail-vue-dm-core'
import router from '..'
import { ROUTES_NAMES } from '../routesNames'
import { logService } from '@nsftx/seven-client-retail-utils'
import errorParser from '@/utils/errorParser'

const LOG_PREFIX = '[lastVisitedRouteGuard]'
const LAST_VISITED_ROUTE_LS_KEY = 'pos_last_visited_route'
// this is mocked for now, it should be replaced with the actual first operation route in the future
const FIRST_OPERATION_ROUTE_NAME = ROUTES_NAMES.settings

const EXCLUDED_ROUTE_NAMES_FROM_SAVING: ReadonlyArray<string> = [
  ROUTES_NAMES.login,
  ROUTES_NAMES.accessDenied,
  ROUTES_NAMES.logout,
  ROUTES_NAMES.home,
] as const

const shouldExcludeRouteFromSaving = (route: RouteLocationNormalizedLoaded) =>
  EXCLUDED_ROUTE_NAMES_FROM_SAVING.includes(route.name)

const saveLastVisitedRoute = (fullPath: string, operatorUuid: string) => {
  const routes = getLastVisitedRoutes()
  routes[operatorUuid] = fullPath
  localStorage.setItem(LAST_VISITED_ROUTE_LS_KEY, JSON.stringify(routes))
}
const getLastVisitedRoutes = (): Record<string, string> => {
  try {
    const parsedRoutes = JSON.parse(localStorage.getItem(LAST_VISITED_ROUTE_LS_KEY) ?? '{}')

    return parsedRoutes !== null && typeof parsedRoutes === 'object' ? parsedRoutes : {}
  } catch (err) {
    logService.warn(
      `${LOG_PREFIX} Failed to parse last visited routes, resetting to empty object.`,
      {
        code: 'SS_LAST_VISITED_ROUTE_PARSE_ERROR',
        ...errorParser.parseUpstream(err),
      },
    )

    return {}
  }
}

const getLastVisitedRoute = (operatorUuid: string): string | null =>
  getLastVisitedRoutes()[operatorUuid] ?? null

const isValidLastVisitedRoute = (fullPath: string) => {
  const { linkGames: games } = storeToRefs(useGamesStore())
  const resolvedRoute = router.resolve(fullPath)

  if (resolvedRoute.name === ROUTES_NAMES.game) {
    return games.value?.some((game) => game.displayId === resolvedRoute.params.gameId)
  }

  return true
}

const lastVisitedRouteGuard = () => {
  const { isOperatorLoggedIn, operatorLoginData } = storeToRefs(useDmCoreSpaceOperatorStore())
  const { linkGames: games } = storeToRefs(useGamesStore())

  router.beforeEach((to, from) => {
    const firstGame = games.value?.[0]
    const operatorUuid = operatorLoginData.value?.operator_uuid
    const lastVisitedRoute = operatorUuid ? getLastVisitedRoute(operatorUuid) : null
    const isLoginTarget = to.name === ROUTES_NAMES.login
    const isPostLoginHomeRedirect =
      from.name === ROUTES_NAMES.login && to.name === ROUTES_NAMES.home

    if (!isOperatorLoggedIn.value || (!isLoginTarget && !isPostLoginHomeRedirect)) {
      return
    }

    if (lastVisitedRoute && isValidLastVisitedRoute(lastVisitedRoute)) {
      return { path: lastVisitedRoute }
    }

    if (firstGame?.displayId) {
      return {
        name: ROUTES_NAMES.game,
        params: {
          gameId: firstGame.displayId,
        },
      }
    }

    return { name: FIRST_OPERATION_ROUTE_NAME }
  })

  router.afterEach((to) => {
    const operatorUuid = operatorLoginData.value?.operator_uuid

    if (!shouldExcludeRouteFromSaving(to) && operatorUuid)
      saveLastVisitedRoute(to.fullPath, operatorUuid)
  })
}

export { lastVisitedRouteGuard }
